<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useUserStore } from '@/stores/user'
import HeaderPanel from '@/components/HeaderPanel.vue'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import MultiSelect from 'primevue/multiselect'
import InputText from 'primevue/inputtext'
import DatePicker from 'primevue/datepicker'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import ToggleSwitch from 'primevue/toggleswitch'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import SelectButton from 'primevue/selectbutton'
import FilterBuilder from '@/components/deal-exports/FilterBuilder.vue'
import {
  cloneExpr,
  emptyFilterGroup,
  exprToSimpleFilters,
  previewFilterExpr,
  simpleFiltersToExpr,
} from '@/utils/dealExportFilters'

const apiBase = 'https://ncht.bz'
const userStore = useUserStore()
const toast = useToast()
const confirm = useConfirm()

const activeTab = ref('orders')
const loading = ref(false)
const exportLoading = ref(false)
const presetsLoading = ref(false)
const logsLoading = ref(false)
const presetRunLoadingId = ref(null)

const rows = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(100)
const freshness = ref(null)

const statusOptions = ref([])
const groupOptions = ref([])
const offerTagOptions = ref([])

const filterMode = ref('simple')
const filterExpr = ref(emptyFilterGroup())
const filterModeOptions = [
  { label: 'Простой', value: 'simple' },
  { label: 'Конструктор', value: 'advanced' },
]

const filters = ref({
  statuses: [],
  title_pattern: '',
  offer_tags: [],
  group_names: [],
  created_from: null,
  created_to: null,
})

const presets = ref([])
const exportLogs = ref([])
const logsTotal = ref(0)
const logsPage = ref(1)
const logsPageSize = ref(50)

const presetDialogVisible = ref(false)
const presetSaving = ref(false)
const editingPresetId = ref(null)
const presetFilterMode = ref('simple')
const presetFilterExpr = ref(emptyFilterGroup())
const presetForm = ref({
  name: '',
  enabled: true,
  spreadsheet_id: '',
  sheet_name: 'Выгрузка',
  filters: {
    statuses: [],
    title_pattern: '',
    offer_tags: [],
    group_names: [],
    created_from: null,
    created_to: null,
  },
})

const tableColumns = [
  { field: 'deal_number', header: 'Номер заказа' },
  { field: 'user_display', header: 'Пользователь' },
  { field: 'email', header: 'Email' },
  { field: 'phone', header: 'Телефон' },
  { field: 'created_at', header: 'Дата создания' },
  { field: 'utm_source', header: 'utm_source' },
  { field: 'utm_medium', header: 'utm_medium' },
  { field: 'utm_campaign', header: 'utm_campaign' },
  { field: 'utm_content', header: 'utm_content' },
  { field: 'utm_term', header: 'utm_term' },
  { field: 'tg_username', header: 'tg_username' },
  { field: 'tg_id', header: 'tg_id' },
  { field: 'sb_id', header: 'sb_id' },
  { field: 'referer', header: 'referer' },
]

const pageSizeOptions = [50, 100, 200]

const authHeaders = () => ({
  Authorization: `Bearer ${userStore.token}`,
})

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

function formatDateParam(value) {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildFiltersPayload(source = filters.value, mode = filterMode.value, expr = filterExpr.value) {
  if (mode === 'advanced') {
    return { mode: 'advanced', expr: cloneExpr(expr) }
  }
  return {
    mode: 'simple',
    statuses: source.statuses?.length ? source.statuses : null,
    title_pattern: source.title_pattern?.trim() || null,
    offer_tags: source.offer_tags?.length ? source.offer_tags : null,
    group_names: source.group_names?.length ? source.group_names : null,
    created_from: formatDateParam(source.created_from),
    created_to: formatDateParam(source.created_to),
    expr: null,
  }
}

function onFilterModeChange(nextMode) {
  if (nextMode === 'advanced') {
    filterExpr.value = simpleFiltersToExpr(filters.value)
    filterMode.value = 'advanced'
    return
  }
  const simple = exprToSimpleFilters(filterExpr.value)
  if (simple) {
    filters.value = simple
    filterMode.value = 'simple'
    return
  }
  confirm.require({
    message: 'Конструктор нельзя свести к простым фильтрам (есть OR/NOT/вложенность). Сбросить конструктор?',
    header: 'Переключение режима',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Сбросить',
    rejectLabel: 'Остаться',
    accept: () => {
      filterExpr.value = emptyFilterGroup()
      filterMode.value = 'simple'
    },
    reject: () => {
      filterMode.value = 'advanced'
    },
  })
}

function onPresetFilterModeChange(nextMode) {
  if (nextMode === 'advanced') {
    presetFilterExpr.value = simpleFiltersToExpr(presetForm.value.filters)
    presetFilterMode.value = 'advanced'
    return
  }
  const simple = exprToSimpleFilters(presetFilterExpr.value)
  if (simple) {
    presetForm.value.filters = simple
    presetFilterMode.value = 'simple'
    return
  }
  confirm.require({
    message: 'Сбросить конструктор пресета и перейти в простой режим?',
    header: 'Переключение режима',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Сбросить',
    rejectLabel: 'Остаться',
    accept: () => {
      presetFilterExpr.value = emptyFilterGroup()
      presetFilterMode.value = 'simple'
    },
    reject: () => {
      presetFilterMode.value = 'advanced'
    },
  })
}

const filterPreview = computed(() => previewFilterExpr(filterExpr.value))

const freshnessLabel = computed(() => {
  const deals = freshness.value?.deals_etl_at
  const users = freshness.value?.users_etl_at
  if (!deals && !users) return 'Свежесть данных неизвестна'
  const latest = [deals, users].filter(Boolean).sort().pop()
  return `Данные обновлены: ${formatDate(latest)} (ETL 10:30/19:00, dbt 11:30/20:00 МСК)`
})

async function loadFilterOptions() {
  const headers = authHeaders()
  const [statusesRes, groupsRes, tagsRes] = await Promise.allSettled([
    axios.get(`${apiBase}/api/deal-exports/filters/statuses`, { headers }),
    axios.get(`${apiBase}/api/deal-exports/filters/groups`, { headers }),
    axios.get(`${apiBase}/api/deal-exports/filters/offer-tags`, { headers }),
  ])

  if (statusesRes.status === 'fulfilled') {
    statusOptions.value = (statusesRes.value.data?.data || []).map((r) => r.status).filter(Boolean)
  } else {
    statusOptions.value = []
    toast.add({ severity: 'warn', summary: 'Статусы', detail: 'Не удалось загрузить', life: 3000 })
  }

  if (groupsRes.status === 'fulfilled') {
    groupOptions.value = (groupsRes.value.data?.data || []).map((r) => r.group_name).filter(Boolean)
  } else {
    groupOptions.value = []
    toast.add({ severity: 'warn', summary: 'Группы', detail: 'Не удалось загрузить', life: 3000 })
  }

  if (tagsRes.status === 'fulfilled') {
    offerTagOptions.value = (tagsRes.value.data?.data || [])
      .map((r) => (typeof r === 'string' ? r : r.offer_tag_name))
      .filter(Boolean)
  } else {
    offerTagOptions.value = []
    toast.add({ severity: 'warn', summary: 'Теги', detail: 'Не удалось загрузить', life: 3000 })
  }
}

async function loadFreshness() {
  try {
    const { data } = await axios.get(`${apiBase}/api/deal-exports/data-freshness`, { headers: authHeaders() })
    freshness.value = data?.data || null
  } catch {
    freshness.value = null
  }
}

async function searchDeals() {
  loading.value = true
  try {
    const { data } = await axios.post(
      `${apiBase}/api/deal-exports/deals/search`,
      {
        filters: buildFiltersPayload(),
        page: page.value,
        limit: pageSize.value,
      },
      { headers: authHeaders() }
    )
    rows.value = data?.data || []
    total.value = data?.meta?.total || 0
  } catch (error) {
    rows.value = []
    total.value = 0
    toast.add({ severity: 'error', summary: 'Поиск', detail: error.response?.data?.message || error.message, life: 5000 })
  } finally {
    loading.value = false
  }
}

function resetFilters() {
  filters.value = {
    statuses: [],
    title_pattern: '',
    offer_tags: [],
    group_names: [],
    created_from: null,
    created_to: null,
  }
  filterExpr.value = emptyFilterGroup()
  page.value = 1
  searchDeals()
}

function onPageChange(event) {
  page.value = Math.floor(event.first / event.rows) + 1
  pageSize.value = event.rows
  searchDeals()
}

async function exportCsv() {
  exportLoading.value = true
  try {
    const payload = buildFiltersPayload()
    let response
    if (filterMode.value === 'advanced') {
      response = await axios.post(
        `${apiBase}/api/deal-exports/deals/export.csv`,
        { filters: payload },
        { headers: authHeaders(), responseType: 'blob' }
      )
    } else {
      const params = new URLSearchParams()
      Object.entries(payload).forEach(([key, val]) => {
        if (val == null || key === 'mode' || key === 'expr') return
        if (Array.isArray(val)) {
          val.forEach((item) => params.append(key, item))
        } else {
          params.set(key, val)
        }
      })
      response = await axios.get(`${apiBase}/api/deal-exports/deals/export.csv?${params}`, {
        headers: authHeaders(),
        responseType: 'blob',
      })
    }
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `deal-exports-${Date.now()}.csv`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    toast.add({ severity: 'error', summary: 'CSV', detail: error.response?.data?.message || 'Ошибка выгрузки', life: 4000 })
  } finally {
    exportLoading.value = false
  }
}

async function loadPresets() {
  presetsLoading.value = true
  try {
    const { data } = await axios.get(`${apiBase}/api/deal-exports/presets`, { headers: authHeaders() })
    presets.value = data?.data || []
  } catch {
    presets.value = []
  } finally {
    presetsLoading.value = false
  }
}

async function loadExportLogs() {
  logsLoading.value = true
  try {
    const offset = (logsPage.value - 1) * logsPageSize.value
    const { data } = await axios.get(`${apiBase}/api/deal-exports/export-logs`, {
      headers: authHeaders(),
      params: { limit: logsPageSize.value, offset },
    })
    exportLogs.value = data?.data || []
    logsTotal.value = data?.meta?.total || 0
  } catch {
    exportLogs.value = []
    logsTotal.value = 0
  } finally {
    logsLoading.value = false
  }
}

function loadPresetFilters(preset) {
  if (preset?.filters?.mode === 'advanced' && preset.filters.expr) {
    presetFilterMode.value = 'advanced'
    presetFilterExpr.value = cloneExpr(preset.filters.expr)
    presetForm.value.filters = {
      statuses: [],
      title_pattern: '',
      offer_tags: [],
      group_names: [],
      created_from: null,
      created_to: null,
    }
    return
  }
  presetFilterMode.value = 'simple'
  presetFilterExpr.value = emptyFilterGroup()
  presetForm.value.filters = {
    statuses: preset?.filters?.statuses || [],
    title_pattern: preset?.filters?.title_pattern || '',
    offer_tags: preset?.filters?.offer_tags || [],
    group_names: preset?.filters?.group_names || [],
    created_from: preset?.filters?.created_from ? new Date(preset.filters.created_from) : null,
    created_to: preset?.filters?.created_to ? new Date(preset.filters.created_to) : null,
  }
}

function openPresetDialog(preset = null) {
  if (preset) {
    editingPresetId.value = preset.id
    presetForm.value = {
      name: preset.name,
      enabled: preset.enabled,
      spreadsheet_id: preset.google_sheet?.spreadsheet_id || '',
      sheet_name: preset.google_sheet?.sheet_name || 'Выгрузка',
      filters: {},
    }
    loadPresetFilters(preset)
  } else {
    editingPresetId.value = null
    presetFilterMode.value = filterMode.value
    presetFilterExpr.value = filterMode.value === 'advanced'
      ? cloneExpr(filterExpr.value)
      : emptyFilterGroup()
    presetForm.value = {
      name: '',
      enabled: true,
      spreadsheet_id: '',
      sheet_name: 'Выгрузка',
      filters: filterMode.value === 'simple' ? { ...filters.value } : {
        statuses: [],
        title_pattern: '',
        offer_tags: [],
        group_names: [],
        created_from: null,
        created_to: null,
      },
    }
  }
  presetDialogVisible.value = true
}

async function savePreset() {
  if (!presetForm.value.name?.trim()) {
    toast.add({ severity: 'warn', summary: 'Пресет', detail: 'Укажите название', life: 3000 })
    return
  }
  presetSaving.value = true
  try {
    const payload = {
      name: presetForm.value.name.trim(),
      enabled: presetForm.value.enabled,
      google_sheet: {
        spreadsheet_id: presetForm.value.spreadsheet_id.trim(),
        sheet_name: presetForm.value.sheet_name.trim() || 'Выгрузка',
      },
      filters: presetFilterMode.value === 'advanced'
        ? buildFiltersPayload(presetForm.value.filters, 'advanced', presetFilterExpr.value)
        : buildFiltersPayload(presetForm.value.filters, 'simple'),
    }
    if (editingPresetId.value) {
      await axios.put(`${apiBase}/api/deal-exports/presets/${editingPresetId.value}`, payload, { headers: authHeaders() })
    } else {
      await axios.post(`${apiBase}/api/deal-exports/presets`, payload, { headers: authHeaders() })
    }
    presetDialogVisible.value = false
    await loadPresets()
    toast.add({ severity: 'success', summary: 'Пресет', detail: 'Сохранено', life: 2500 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Пресет', detail: error.response?.data?.message || error.message, life: 4000 })
  } finally {
    presetSaving.value = false
  }
}

function confirmDeletePreset(preset) {
  confirm.require({
    message: `Удалить пресет «${preset.name}»?`,
    header: 'Подтверждение',
    icon: 'pi pi-exclamation-triangle',
    accept: async () => {
      try {
        await axios.delete(`${apiBase}/api/deal-exports/presets/${preset.id}`, { headers: authHeaders() })
        await loadPresets()
        toast.add({ severity: 'success', summary: 'Пресет', detail: 'Удалён', life: 2500 })
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Пресет', detail: error.message, life: 4000 })
      }
    },
  })
}

async function runPreset(preset) {
  presetRunLoadingId.value = preset.id
  try {
    const { data } = await axios.post(`${apiBase}/api/deal-exports/presets/${preset.id}/run`, {}, { headers: authHeaders() })
    toast.add({
      severity: 'success',
      summary: 'Выгрузка',
      detail: `Готово: ${data?.data?.rowCount ?? 0} строк`,
      life: 4000,
    })
    await Promise.all([loadPresets(), loadExportLogs()])
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Выгрузка', detail: error.response?.data?.message || error.message, life: 5000 })
  } finally {
    presetRunLoadingId.value = null
  }
}

async function togglePresetEnabled(preset) {
  try {
    await axios.put(
      `${apiBase}/api/deal-exports/presets/${preset.id}`,
      { enabled: !preset.enabled },
      { headers: authHeaders() }
    )
    await loadPresets()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Пресет', detail: error.message, life: 4000 })
  }
}

function onLogsPageChange(event) {
  logsPage.value = Math.floor(event.first / event.rows) + 1
  logsPageSize.value = event.rows
  loadExportLogs()
}

onMounted(async () => {
  await userStore.checkAuth()
  await Promise.all([loadFilterOptions(), loadFreshness(), loadPresets(), loadExportLogs()])
  await searchDeals()
})
</script>

<template>
  <HeaderPanel />
  <div class="deal-exports-page">
    <header class="page-header">
      <h1>Выгрузки заказов</h1>
      <Message v-if="freshness" severity="info" :closable="false">{{ freshnessLabel }}</Message>
    </header>

    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab value="orders">Заказы</Tab>
        <Tab value="presets">Автовыгрузки</Tab>
        <Tab value="logs">Логи</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="orders">
          <div class="filters-panel">
            <div class="mode-row">
              <label>Режим фильтра</label>
              <SelectButton
                :modelValue="filterMode"
                :options="filterModeOptions"
                optionLabel="label"
                optionValue="value"
                @update:modelValue="onFilterModeChange"
              />
            </div>

            <template v-if="filterMode === 'simple'">
              <div class="filter-row">
                <label>Статус</label>
                <MultiSelect
                  v-model="filters.statuses"
                  :options="statusOptions"
                  placeholder="Все статусы"
                  display="chip"
                  class="filter-control"
                />
              </div>
              <div class="filter-row">
                <label>Состав заказа</label>
                <InputText v-model="filters.title_pattern" placeholder="Поиск по названию" class="filter-control" />
              </div>
              <div class="filter-row">
                <label>Теги предложений</label>
                <MultiSelect
                  v-model="filters.offer_tags"
                  :options="offerTagOptions"
                  placeholder="Все теги"
                  display="chip"
                  filter
                  class="filter-control"
                />
              </div>
              <div class="filter-row">
                <label>Группа пользователя</label>
                <MultiSelect
                  v-model="filters.group_names"
                  :options="groupOptions"
                  placeholder="Все группы"
                  display="chip"
                  filter
                  class="filter-control"
                />
              </div>
              <div class="filter-row dates">
                <label>Дата создания</label>
                <DatePicker v-model="filters.created_from" dateFormat="yy-mm-dd" placeholder="От" showIcon />
                <DatePicker v-model="filters.created_to" dateFormat="yy-mm-dd" placeholder="До" showIcon />
              </div>
            </template>

            <template v-else>
              <FilterBuilder
                v-model="filterExpr"
                :statusOptions="statusOptions"
                :groupOptions="groupOptions"
                :offerTagOptions="offerTagOptions"
              />
              <p class="preview-line"><strong>Превью:</strong> {{ filterPreview }}</p>
            </template>

            <div class="filter-actions">
              <Button label="Найти" icon="pi pi-search" :loading="loading" @click="page = 1; searchDeals()" />
              <Button label="Сброс" severity="secondary" outlined @click="resetFilters" />
              <Button label="CSV" icon="pi pi-download" severity="help" outlined :loading="exportLoading" @click="exportCsv" />
              <Button label="Сохранить как пресет" icon="pi pi-save" severity="secondary" text @click="openPresetDialog()" />
            </div>
          </div>

          <div class="meta-line">Всего: {{ total }}</div>

          <DataTable
            :value="rows"
            :loading="loading"
            paginator
            lazy
            :rows="pageSize"
            :totalRecords="total"
            :rowsPerPageOptions="pageSizeOptions"
            @page="onPageChange"
            scrollable
            scrollHeight="flex"
            size="small"
            class="deals-table"
          >
            <Column
              v-for="col in tableColumns"
              :key="col.field"
              :field="col.field"
              :header="col.header"
              :style="col.field === 'created_at' ? 'min-width: 10rem' : 'min-width: 8rem'"
            >
              <template v-if="col.field === 'created_at'" #body="{ data }">
                {{ formatDate(data.created_at) }}
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <TabPanel value="presets">
          <div class="panel-toolbar">
            <Button label="Новый пресет" icon="pi pi-plus" @click="openPresetDialog()" />
            <Button label="Обновить" icon="pi pi-refresh" severity="secondary" text :loading="presetsLoading" @click="loadPresets" />
          </div>
          <DataTable :value="presets" :loading="presetsLoading" size="small">
            <Column field="name" header="Название" />
            <Column field="enabled" header="Вкл">
              <template #body="{ data }">
                <ToggleSwitch :modelValue="data.enabled" @update:modelValue="togglePresetEnabled(data)" />
              </template>
            </Column>
            <Column field="last_export_at" header="Последняя выгрузка">
              <template #body="{ data }">{{ formatDate(data.last_export_at) }}</template>
            </Column>
            <Column field="last_export_rows" header="Строк" />
            <Column field="last_export_status" header="Статус">
              <template #body="{ data }">
                <Tag
                  :value="data.last_export_status || '—'"
                  :severity="data.last_export_status === 'success' ? 'success' : data.last_export_status === 'error' ? 'danger' : 'secondary'"
                />
              </template>
            </Column>
            <Column header="Действия" style="min-width: 14rem">
              <template #body="{ data }">
                <Button
                  icon="pi pi-play"
                  label="Run"
                  size="small"
                  text
                  :loading="presetRunLoadingId === data.id"
                  @click="runPreset(data)"
                />
                <Button icon="pi pi-pencil" size="small" text @click="openPresetDialog(data)" />
                <Button icon="pi pi-trash" size="small" text severity="danger" @click="confirmDeletePreset(data)" />
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <TabPanel value="logs">
          <DataTable
            :value="exportLogs"
            :loading="logsLoading"
            paginator
            lazy
            :rows="logsPageSize"
            :totalRecords="logsTotal"
            @page="onLogsPageChange"
            size="small"
          >
            <Column field="started_at" header="Начало">
              <template #body="{ data }">{{ formatDate(data.started_at) }}</template>
            </Column>
            <Column field="preset_name" header="Пресет" />
            <Column field="row_count" header="Строк" />
            <Column field="status" header="Статус">
              <template #body="{ data }">
                <Tag :value="data.status" :severity="data.status === 'success' ? 'success' : 'danger'" />
              </template>
            </Column>
            <Column field="error_message" header="Ошибка" />
          </DataTable>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <Dialog
      v-model:visible="presetDialogVisible"
      :header="editingPresetId ? 'Редактировать пресет' : 'Новый пресет'"
      modal
      class="preset-dialog"
      :style="{ width: 'min(720px, 96vw)' }"
    >
      <div class="preset-form">
        <label>Название</label>
        <InputText v-model="presetForm.name" class="w-full" />
        <label>Spreadsheet ID</label>
        <InputText v-model="presetForm.spreadsheet_id" class="w-full" />
        <label>Имя листа</label>
        <InputText v-model="presetForm.sheet_name" class="w-full" />
        <div class="enabled-row">
          <label>Включён</label>
          <ToggleSwitch v-model="presetForm.enabled" />
        </div>
        <h4>Фильтры пресета</h4>
        <SelectButton
          :modelValue="presetFilterMode"
          :options="filterModeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          @update:modelValue="onPresetFilterModeChange"
        />
        <template v-if="presetFilterMode === 'simple'">
          <MultiSelect v-model="presetForm.filters.statuses" :options="statusOptions" placeholder="Статусы" display="chip" class="w-full" />
          <InputText v-model="presetForm.filters.title_pattern" placeholder="Состав заказа" class="w-full" />
          <MultiSelect v-model="presetForm.filters.offer_tags" :options="offerTagOptions" placeholder="Теги" display="chip" filter class="w-full" />
          <MultiSelect v-model="presetForm.filters.group_names" :options="groupOptions" placeholder="Группы" display="chip" filter class="w-full" />
          <div class="dates">
            <DatePicker v-model="presetForm.filters.created_from" dateFormat="yy-mm-dd" placeholder="Дата от" showIcon />
            <DatePicker v-model="presetForm.filters.created_to" dateFormat="yy-mm-dd" placeholder="Дата до" showIcon />
          </div>
        </template>
        <FilterBuilder
          v-else
          v-model="presetFilterExpr"
          :statusOptions="statusOptions"
          :groupOptions="groupOptions"
          :offerTagOptions="offerTagOptions"
        />
      </div>
      <template #footer>
        <Button label="Отмена" severity="secondary" text @click="presetDialogVisible = false" />
        <Button label="Сохранить" :loading="presetSaving" @click="savePreset" />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.deal-exports-page {
  padding: 1rem 1.25rem 2rem;
  max-width: 100%;
}

.page-header {
  margin-bottom: 1rem;
}

.page-header h1 {
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
}

.filters-panel {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 8px;
}

.filter-row {
  display: grid;
  gap: 0.35rem;
}

.filter-row.dates,
.dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.filter-control {
  width: 100%;
  max-width: 480px;
}

.filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mode-row {
  display: grid;
  gap: 0.35rem;
}

.preview-line {
  margin: 0;
  font-size: 0.85rem;
  color: #475569;
}

.meta-line {
  margin-bottom: 0.5rem;
  color: #64748b;
  font-size: 0.9rem;
}

.panel-toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.deals-table {
  min-height: 320px;
}

.preset-form {
  display: grid;
  gap: 0.65rem;
}

.preset-form h4 {
  margin: 0.5rem 0 0;
}

.enabled-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.w-full {
  width: 100%;
}
</style>
